import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLoaderData, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ReserveOptions,
  ReservesOptionsParameters,
  ReservesOptionsReturn,
} from "@tlc/ls2pac/components/ReservesSearchForm";
import { ReservesSearchParameters } from "./ReservesSearchParameters";
import { useBranchLimits } from "@tlc/ls2pac/components/BranchLimits";
import { ReservesSearchSortType } from "./ReservesSearchSortType";
import { LoaderData } from "../../account/MyAccount";
import { reservesLoader } from "../reservesLoader";
import { SearchResultView } from "../SearchResultView";
import { ResourceView } from "@tlc/ls2pac/dto/ResourceView";

export const ReservesOptionsHolder = (() => {
  const optionsCache = new Map<string, Promise<ReserveOptions>>();

  const getOptions = async (
    params: ReservesOptionsParameters
  ): Promise<ReserveOptions> => {
    const paramsKey = JSON.stringify(params);

    if (!optionsCache.has(paramsKey)) {
      const request = axios
        .post<ReservesOptionsReturn>("/reserves/options", params)
        .then(({ data }) => {
          if (data.success) {
            return data.options;
          } else {
            console.error("ReservesOptionsReturn failure", data);
            throw new Error("Failed to fetch reserve options");
          }
        })
        .catch((error) => {
          optionsCache.delete(paramsKey);
          throw error;
        });

      optionsCache.set(paramsKey, request);
    }

    return optionsCache.get(paramsKey)!;
  };

  return {
    getAllOptions: (): Promise<ReserveOptions> => {
      return getOptions({
        branchFilters: [],
        targetAudience: "",
        department: "",
        course: "",
        instructor: "",
      });
    },

    getOptions: (
      params: ReservesOptionsParameters
    ): Promise<ReserveOptions> => {
      return getOptions(params);
    },
  };
})();

interface ReservesSearchParams extends ReservesOptionsParameters {
  page: number;
  sortKey: string;
  sort: ReservesSearchSortType;
}

interface ReservesSearchContextType {
  setPage: (page: number) => void;
  setSort: (sort: ReservesSearchSortType) => void;
  setDepartment: (department: string) => void;
  setCourse: (course: string) => void;
  setInstructor: (instructor: string) => void;
  clearReservesParams: () => void;
  updateSearchParam: (key: string, value: string) => void;
  setBranchFilters: (branchIds: string[]) => void;
  searchParams: ReservesSearchParams;
  totalHits: Readonly<number | null>;
  results: Readonly<ResourceView[] | null>;
  resultsKey: Readonly<string | null>;
  loadingResults: Readonly<boolean>;
  pageSize: number;
  options: Readonly<ReserveOptions | null>;
  allOptions: Readonly<ReserveOptions | null>;
  loadingOptions: Readonly<boolean>;
  hasActiveFilters: Readonly<boolean>;
}

export const useReservesSearch = () => {
  const context = useContext(ReservesSearchContext);

  if (!context) {
    throw new Error(
      "useReservesSearch must be used within an ReservesProvider"
    );
  }

  return context;
};

const ReservesSearchContext = createContext<
  ReservesSearchContextType | undefined
>(undefined);

export const ReservesProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { allOptions } = useLoaderData() as LoaderData<typeof reservesLoader>;

  const [searchParams, setSearchParams] = useSearchParams();
  const [loadingResults, setLoadingResults] = useState<boolean>(false);
  const [totalHits, setTotalHits] = useState<number | null>(null);
  const [results, setResults] = useState<ResourceView[] | null>(null);
  const pageSize = 12;

  const [options, setOptions] = useState<ReserveOptions | null>(null);

  const [loadingOptions, setLoadingOptions] = useState<boolean>(false);
  const { updateSelectedBranches, selectedBranchIds } = useBranchLimits();

  const resultsKey = useMemo(() => {
    if (!results) {
      return null;
    }
    return results.map((r) => r.id).join(",");
  }, [results]);

  const setPage = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", page.toString());
    setSearchParams(newParams);
  };

  const setSort = (sort: ReservesSearchSortType) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("sortKey", sort);
    newParams.delete("page");
    setSearchParams(newParams);
  };

  const setBranchFilters = (branchIds: string[]) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("page");

    if (!branchIds || branchIds.length === 0) {
      newParams.set("branchFilters", JSON.stringify([])); // need to explicitly set to [] to clear because of context fallback
    } else {
      newParams.set("branchFilters", JSON.stringify(branchIds));
    }

    setSearchParams(newParams);
  };

  const updateSearchParam = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.delete("page");
    setSearchParams(newParams);
  };

  const setDepartment = (dept: string) => {
    updateSearchParam("department", dept);
  };

  const setCourse = (course: string) => {
    updateSearchParam("course", course);
  };

  const setInstructor = (instructor: string) => {
    updateSearchParam("instructor", instructor);
  };

  const clearReservesParams = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("instructor");
    newParams.delete("course");
    newParams.delete("department");
    newParams.delete("page");
    newParams.set("branchFilters", JSON.stringify([]));
    updateSelectedBranches([]);
    setSearchParams(newParams);
  };

  const state = useMemo(() => {
    const pageParam = parseInt(searchParams.get("page") || "1", 10);
    const page = pageParam < 1 ? 1 : pageParam;
    const department = searchParams.get("department") || "";
    const course = searchParams.get("course") || "";
    const instructor = searchParams.get("instructor") || "";
    const targetAudience = searchParams.get("targetAudience") || "";
    const sortKey = searchParams.get("sortKey") || "Title";
    const sort: ReservesSearchSortType =
      sortKey?.toLowerCase() === "author"
        ? ReservesSearchSortType.Author
        : ReservesSearchSortType.Title;
    const branchFiltersStr = searchParams.get("branchFilters");
    const branchFilters = branchFiltersStr ? JSON.parse(branchFiltersStr) : [];

    return {
      page,
      department,
      course,
      instructor,
      targetAudience,
      sortKey,
      sort,
      branchFilters,
    };
  }, [searchParams]);

  useEffect(() => {
    const { department, course, instructor, branchFilters } = state;

    setLoadingOptions(true);

    const reservesOptionsParameters: ReservesOptionsParameters = {
      branchFilters: branchFilters,
      targetAudience: "",
      department: department,
      course: course,
      instructor: instructor,
    };

    ReservesOptionsHolder.getOptions(reservesOptionsParameters)
      .then((data) => {
        setOptions(data);
        setLoadingOptions(false);
      })
      .catch((e) => {
        console.error("Error fetching reserve options", e);
        setLoadingOptions(false);
      });
  }, [state.department, state.course, state.instructor, state.branchFilters]);

  useEffect(() => {
    setLoadingResults(true);
    search(state).then((response) => {
      setTotalHits(response.totalHits);
      setResults(response.resources);
      setLoadingResults(false);
    });
  }, [
    state.page,
    state.department,
    state.course,
    state.instructor,
    state.targetAudience,
    state.branchFilters,
    selectedBranchIds,
  ]);

  useEffect(() => {
    updateSelectedBranches(state.branchFilters);
  }, []);

  const search = async (params: ReservesSearchParams) => {
    const startIndex = params.page <= 1 ? 0 : (params.page - 1) * pageSize;
    const sortType: ReservesSearchSortType =
      params.sortKey?.toLowerCase() === "author"
        ? ReservesSearchSortType.Author
        : ReservesSearchSortType.Title;

    const branchFiltersStr = searchParams.get("branchFilters");
    const hasBranchFiltersParam = branchFiltersStr !== null;

    const searchBranchIds = hasBranchFiltersParam
      ? params.branchFilters
      : selectedBranchIds;

    const payload: ReservesSearchParameters = {
      branchFilters: searchBranchIds || [],
      targetAudience: params.targetAudience,
      sortType: sortType,
      departmentFilter: params.department,
      courseFilter: params.course,
      instructorFilter: params.instructor,
      searchTerm: "",
      startIndex,
      hitsPerPage: pageSize,
    };

    try {
      const { data } = await axios.post<SearchResultView>(
        `/reserves/search`,
        payload
      );
      return data;
    } catch (e: unknown) {
      console.error("Search error:", e);
      if (axios.isAxiosError(e)) {
        console.error("Error response:", e.response?.data);
      }
      return {
        success: false,
        totalHits: 0,
        resources: [] as ResourceView[],
      };
    }
  };

  const contextValue: ReservesSearchContextType = useMemo(() => {
    const { department, instructor, course, branchFilters } = state;
    const hasActiveFilters =
      !!department ||
      !!course ||
      !!instructor ||
      (branchFilters && branchFilters.length > 0) ||
      (selectedBranchIds && selectedBranchIds.length > 0);

    return {
      setPage,
      setSort,
      setDepartment,
      setCourse,
      setInstructor,
      clearReservesParams,
      updateSearchParam,
      setBranchFilters,
      searchParams: {
        page: state.page,
        department,
        course,
        instructor,
        targetAudience: state.targetAudience,
        sortKey: state.sortKey,
        sort: (state.sortKey === "Author"
          ? "Author"
          : "Title") as ReservesSearchSortType,
        branchFilters,
      },
      totalHits,
      results,
      resultsKey,
      loadingResults,
      pageSize,
      options,
      allOptions,
      loadingOptions,
      hasActiveFilters,
      selectedBranchIds,
    };
  }, [
    state.page,
    state.department,
    state.course,
    state.instructor,
    state.targetAudience,
    state.sortKey,
    state.branchFilters,
    totalHits,
    results,
    resultsKey,
    loadingResults,
    options,
    allOptions,
    loadingOptions,
  ]);

  return (
    <ReservesSearchContext.Provider value={contextValue}>
      {children}
    </ReservesSearchContext.Provider>
  );
};

export default ReservesSearchContext;
